import { useCallback, useId, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  HubSupportTicketMessageThread,
} from "@/components/HubSupportTicketMessageThread";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { MRP_LOCATIONS, mrpLocationLabelForCode, type MrpLocation } from "@/lib/mrp";
import {
  formatPurchaseRequestQuantity,
  purchaseRequestIsInventoryReview,
  purchaseRequestItemLabel,
  purchaseRequestKindLabel,
  purchaseRequestMessagesAsHubThread,
  purchaseReviewOutcomeLabel,
  type PurchaseRequestMessage,
  type PurchaseRequestRow,
  type PurchaseRequestStatus,
} from "@/lib/purchaseRequest";
import { formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Request items for purchasing to order, or track inventory review requests created when you approve inventory discrepancies. Open staff requests appear on MRP Safety stock alerts; inventory review follow-ups are on Manufacturing management, Inventory discrepancies.";

function PurchaseRequestStatusBadge({ status }: { status: PurchaseRequestStatus }): JSX.Element {
  if (status === "ordered") {
    return <Badge tone="success">Ordered</Badge>;
  }
  if (status === "reviewed") {
    return <Badge tone="success">Reviewed</Badge>;
  }
  if (status === "refused") {
    return <Badge tone="danger">Refused</Badge>;
  }
  if (status === "cancelled") {
    return <Badge tone="neutral">Cancelled</Badge>;
  }
  return <Badge tone="warning">Open</Badge>;
}

function PurchaseRequestListPage(): JSX.Element {
  const navigate = useNavigate();
  const listQuery = useQuery({
    queryKey: ["mrp", "purchase-requests", "mine"],
    queryFn: () => apiFetch<{ rows: PurchaseRequestRow[] }>("/api/mrp/purchase-requests/mine"),
  });
  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>My purchase requests</PageTitleWithHelp>
        <Button type="button" onClick={() => navigate("/personal/purchase-requests/new")}>
          New request
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Your requests</CardTitle>
        </CardHeader>
        <CardBody className="p-0 sm:p-0">
          {listQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading your requests…</p>
          ) : listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-600">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-600">
              You have not submitted any purchase requests yet. Use New request when you need something ordered.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/personal/purchase-requests/${encodeURIComponent(row.id)}`}
                    className="flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">Request #{row.id}</span>
                      {purchaseRequestIsInventoryReview(row) ? (
                        <Badge tone="neutral">{purchaseRequestKindLabel(row.kind)}</Badge>
                      ) : null}
                      <PurchaseRequestStatusBadge status={row.status} />
                      <span className="text-xs text-slate-500">Qty {formatPurchaseRequestQuantity(row)}</span>
                    </div>
                    <p className="line-clamp-2 text-sm text-slate-700">{purchaseRequestItemLabel(row)}</p>
                    <p className="text-xs text-slate-500">Submitted {formatTimestamp(row.created_at)}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function PurchaseRequestDetailPage(): JSX.Element {
  const { id: requestId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["mrp", "purchase-requests", "mine", "detail", requestId],
    queryFn: () =>
      apiFetch<{ request: PurchaseRequestRow; messages: PurchaseRequestMessage[] }>(
        `/api/mrp/purchase-requests/${encodeURIComponent(requestId)}`,
      ),
    enabled: requestId !== "",
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ request: PurchaseRequestRow }>(`/api/mrp/purchase-requests/${encodeURIComponent(requestId)}`, {
        method: "PATCH",
        body: { status: "cancelled" },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      navigate("/personal/purchase-requests");
    },
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) =>
      apiFetch<{ ok: boolean; messages: PurchaseRequestMessage[] }>(
        `/api/mrp/purchase-requests/${encodeURIComponent(requestId)}/messages`,
        { method: "POST", body: { body } },
      ),
    onSuccess: (result) => {
      setReplyDraft("");
      setReplyError(null);
      queryClient.setQueryData(
        ["mrp", "purchase-requests", "mine", "detail", requestId],
        (previous: { request: PurchaseRequestRow; messages: PurchaseRequestMessage[] } | undefined) =>
          previous ? { ...previous, messages: result.messages } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-requests", "mine"] });
    },
    onError: (error: Error) => {
      setReplyError(error.message);
    },
  });

  const request = detailQuery.data?.request;
  const messages = detailQuery.data?.messages ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/personal/purchase-requests")}>
          Back to list
        </Button>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>
          {request ? `Request #${request.id}` : "Purchase request"}
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardBody className="space-y-4">
          {detailQuery.isLoading && !request ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : detailQuery.isError ? (
            <p className="text-sm text-red-600">{(detailQuery.error as Error).message}</p>
          ) : request == null ? (
            <p className="text-sm text-slate-600">Request not found.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {purchaseRequestIsInventoryReview(request) ? (
                  <Badge tone="neutral">{purchaseRequestKindLabel(request.kind)}</Badge>
                ) : null}
                <PurchaseRequestStatusBadge status={request.status} />
                <span className="text-xs text-slate-500">Submitted {formatTimestamp(request.created_at)}</span>
              </div>

              {purchaseRequestIsInventoryReview(request) ? (
                <p className="rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-700">
                  Purchasing will review open purchase orders and requests for this SKU after the inventory change you
                  approved. You will be notified when they record an outcome.
                </p>
              ) : null}

              <dl className="grid gap-3 text-sm">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Item</dt>
                  <dd className="mt-1 text-slate-900">{purchaseRequestItemLabel(request)}</dd>
                </div>
                {request.sku != null && request.item_description.trim() !== "" ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Description</dt>
                    <dd className="mt-1 text-slate-900">{request.item_description}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {purchaseRequestIsInventoryReview(request) ? "Inventory change" : "Quantity"}
                  </dt>
                  <dd className="mt-1 tabular-nums text-slate-900">{formatPurchaseRequestQuantity(request)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Receiving site</dt>
                  <dd className="mt-1 text-slate-900">{mrpLocationLabelForCode(request.location_code as MrpLocation)}</dd>
                </div>
                {request.page_path != null && request.page_path.trim() !== "" ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Source</dt>
                    <dd className="mt-1">
                      <Link to={request.page_path} className="text-brand-700 hover:underline">
                        View inventory discrepancy
                      </Link>
                    </dd>
                  </div>
                ) : null}
                {request.notes != null && request.notes.trim() !== "" ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {purchaseRequestIsInventoryReview(request) ? "Context" : "Your notes"}
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap text-slate-900">{request.notes}</dd>
                  </div>
                ) : null}
                {request.status === "reviewed" && request.review_outcome != null ? (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50/80 p-3 sm:col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-emerald-800">Review outcome</dt>
                    <dd className="mt-1 text-sm text-emerald-950">
                      {purchaseReviewOutcomeLabel(request.review_outcome)}
                    </dd>
                    {request.review_outcome_note != null && request.review_outcome_note.trim() !== "" ? (
                      <dd className="mt-2 whitespace-pre-wrap text-sm text-emerald-950">
                        {request.review_outcome_note}
                      </dd>
                    ) : null}
                  </div>
                ) : null}
                {request.status === "ordered" ? (
                  <>
                    {request.expected_arrival_date != null ? (
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Expected arrival</dt>
                        <dd className="mt-1 text-slate-900">{request.expected_arrival_date}</dd>
                      </div>
                    ) : null}
                    {request.purchased_from != null && request.purchased_from.trim() !== "" ? (
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Purchased from</dt>
                        <dd className="mt-1 text-slate-900">{request.purchased_from}</dd>
                      </div>
                    ) : null}
                    {request.tracking_number != null && request.tracking_number.trim() !== "" ? (
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Tracking</dt>
                        <dd className="mt-1 font-mono text-slate-900">
                          {request.tracking_carrier != null && request.tracking_carrier.trim() !== ""
                            ? `${request.tracking_carrier} · ${request.tracking_number}`
                            : request.tracking_number}
                        </dd>
                      </div>
                    ) : null}
                  </>
                ) : null}
                {request.status === "refused" && request.refusal_reason != null ? (
                  <div className="rounded-md border border-red-100 bg-red-50/80 p-3">
                    <dt className="text-xs font-medium uppercase tracking-wide text-red-800">Refusal reason</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-sm text-red-950">{request.refusal_reason}</dd>
                  </div>
                ) : null}
              </dl>

              <HubSupportTicketMessageThread
                messages={purchaseRequestMessagesAsHubThread(messages)}
                draft={replyDraft}
                onDraftChange={setReplyDraft}
                onSubmit={() => {
                  void replyMutation.mutateAsync(replyDraft.trim());
                }}
                submitting={replyMutation.isPending}
                canReply={request.status === "open"}
                error={replyError}
                composeLabel={
                  purchaseRequestIsInventoryReview(request) ? "Reply to purchasing" : "Reply to purchasing"
                }
                viewer="reporter"
                staffPartyLabel="Purchasing"
              />

              {request.status === "open" && !purchaseRequestIsInventoryReview(request) ? (
                <div className="flex justify-end border-t border-slate-100 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={cancelMutation.isPending}
                    onClick={() => {
                      void cancelMutation.mutateAsync();
                    }}
                  >
                    {cancelMutation.isPending ? "Cancelling…" : "Cancel request"}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function PurchaseRequestNewPage(): JSX.Element {
  const formId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [itemDescription, setItemDescription] = useState("");
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [locationCode, setLocationCode] = useState<MrpLocation>("sgs");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const parsedQuantity = Number.parseInt(quantity, 10);
      if (!Number.isInteger(parsedQuantity) || parsedQuantity <= 0) {
        setError("Quantity must be a positive whole number.");
        return;
      }
      const result = await apiFetch<{ ok: boolean; id: string }>("/api/mrp/purchase-requests", {
        method: "POST",
        body: {
          item_description: itemDescription,
          sku: sku.trim() === "" ? null : sku.trim(),
          quantity: parsedQuantity,
          location_code: locationCode,
          notes: notes.trim() === "" ? null : notes.trim(),
        },
      });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      showToast({
        message: `Purchase request #${result.id} was submitted.`,
        tone: "success",
      });
      navigate(`/personal/purchase-requests/${encodeURIComponent(result.id)}`);
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Could not submit purchase request.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [itemDescription, sku, quantity, locationCode, notes, navigate, queryClient, showToast]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/personal/purchase-requests")}>
          Back to list
        </Button>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>New purchase request</PageTitleWithHelp>
      </header>

      <Card>
        <CardBody>
          <form
            id={formId}
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void onSubmit();
            }}
          >
            <div>
              <Label htmlFor={`${formId}-description`}>What do you need ordered?</Label>
              <textarea
                id={`${formId}-description`}
                required
                rows={3}
                value={itemDescription}
                onChange={(event) => setItemDescription(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Part description, vendor link, or other ordering detail…"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor={`${formId}-sku`}>SKU (optional)</Label>
                <Input
                  id={`${formId}-sku`}
                  value={sku}
                  onChange={(event) => setSku(event.target.value)}
                  className="mt-1 font-mono"
                  placeholder="Catalog SKU if known"
                />
              </div>
              <div>
                <Label htmlFor={`${formId}-quantity`}>Quantity</Label>
                <Input
                  id={`${formId}-quantity`}
                  type="number"
                  min={1}
                  step={1}
                  required
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor={`${formId}-location`}>Receiving site</Label>
              <select
                id={`${formId}-location`}
                required
                value={locationCode}
                onChange={(event) => setLocationCode(event.target.value as MrpLocation)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {MRP_LOCATIONS.map((location) => (
                  <option key={location.code} value={location.code}>
                    {location.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor={`${formId}-notes`}>Notes (optional)</Label>
              <textarea
                id={`${formId}-notes`}
                rows={2}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Urgency, job reference, preferred vendor…"
              />
            </div>
            {error != null ? <p className="text-sm text-red-600">{error}</p> : null}
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <Button type="button" variant="secondary" onClick={() => navigate("/personal/purchase-requests")}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || itemDescription.trim() === ""}>
                {submitting ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

export function PurchaseRequestsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<PurchaseRequestListPage />} />
      <Route path="new" element={<PurchaseRequestNewPage />} />
      <Route path=":id" element={<PurchaseRequestDetailPage />} />
      <Route path="*" element={<Navigate to="/personal/purchase-requests" replace />} />
    </Routes>
  );
}
